/**
 * A script for mattress products with hybrid upsell options.
 */

/**
 * Holds state functions and objects related to hybrid foam upsells on mattresses.
 * Sort of like an extension interface of NolahPDPState (but we don't go so far
 * as to inherit it like a child class).
 */
window.HybridUpsellState = {
  /**
   * @param PDPState NolahPDPState from product-card is a dependency.
   */
  init(PDPState) {
    if (!PDPState) {
      throw new Error('PDPState undefined');
    }
    this.PDPState = PDPState;

    this.foamProductJSON = window.NolahProductJSON.product;
    this.hybridProductJSON = JSON.parse(
      document.querySelector('[data-hybrid-product-json]').innerHTML
    );

    // selectedValue can be 'foam' or 'hybrid'
    this.selectedValue = 'foam';
  },

  selectHybridUpsell() {
    this.selectedValue = 'hybrid';
    this.PDPState.setProductJSON(this.hybridProductJSON);
  },

  selectFoamUpsell() {
    this.selectedValue = 'foam';
    this.PDPState.setProductJSON(this.foamProductJSON);
  },

  select(value) {
    if (value !== 'foam' && value !== 'hybrid') {
      throw new Error('Incorrect hybrid foam upsell value given to state object');
    }

    value === 'foam' ? this.selectFoamUpsell() : this.selectHybridUpsell();
  }
};
window.HybridUpsellState.init(window.NolahPDPState);

class HybridUpsellButtons {
  /**
   * @param el Container element with MattressHybridArea__Btns inside.
   */
  constructor(el) {
    this.el = el;
    this.state = {
      value: 'foam',
    };

    this.cacheElements();
    this.attachListeners();

    // Sync initial state with HybridUpsellState
    this.selectValue(this.state.value);
  }

  cacheElements() {
    this.btns = this.el.querySelectorAll('.MattressHybridArea__Btn');
    this.foamBtn = this.el.querySelector(
      '.MattressHybridArea__Btn[data-value="foam"]'
    );
    this.hybridBtn = this.el.querySelector(
      '.MattressHybridArea__Btn[data-value="hybrid"]'
    );
    this.selectBtn = this.selectBtn.bind(this);
  }

  attachListeners() {
    document.addEventListener('state:option-changed', e => {
      if (e && e.detail && e.detail.productJSONChanged) {
        // Quietly update the buttons
        const value = window.HybridUpsellState.selectedValue;
        this.selectValue(value, true /* silent */);
      }
    });

    this.btns.forEach(btn => {
      btn.addEventListener('click', e => {
        const value = e.currentTarget.dataset.value;
        window.HybridUpsellState.select(value);
      });
    });
  }

  selectValue(value) {
    const foundEl = Array.from(this.btns).find(b => b.dataset.value === value);
    this.selectBtn(foundEl);
  }

  selectBtn(btn) {
    // Remember which value was selected
    this.state.value = btn.dataset.value;

    // Update how it looks
    this.btns.forEach(b =>
      b.classList.remove('MattressHybridArea__Btn--added')
    );
    btn.classList.add('MattressHybridArea__Btn--added');

    // Update contents of price labels
    this.updatePriceLabels();
  }

  /**
   * Update the labels to indicate the additional cost the hybrid upsell represents
   * in the button.
   */
  updatePriceLabels() {
    const foamPriceLabel = this.foamBtn.querySelector(
      '.MattressHybridArea__PriceTag'
    );
    const hybridPriceLabel = this.hybridBtn.querySelector(
      '.MattressHybridArea__PriceTag'
    );

    if (this.state.value === 'foam') {
      foamPriceLabel.innerHTML =
        '<span class="MattressHybridArea__Price">+$0</span>';
      hybridPriceLabel.innerHTML =
        '<span class="MattressHybridArea__Compare StrikeLabel">+$199</span> <span class="MattressHybridArea__Price">+$140</span>';
    } else {
      foamPriceLabel.innerHTML =
        '<span class="MattressHybridArea__Price">-$140</span>';
      hybridPriceLabel.innerHTML =
        '<span class="MattressHybridArea__Compare StrikeLabel">+$199</span> <span class="MattressHybridArea__Price">+$0</span>';
    }
  }
}

class HugeHybridToggle extends HTMLElement {
  constructor() {
    super();
    this.initalized = false;
  }

  cacheElements() {
    this.optionBtns = this.querySelectorAll('.HugeHybridToggle__OptionBtn');
    this.peg = this.querySelector('.HugeHybridToggle__Peg');
    this.onBtnClick = this.onBtnClick.bind(this);
    this.lock = this.lock.bind(this);
    this.move = this.move.bind(this);
    this.swipeX = null;

    // Set initial value
    const selectedBtn = this.querySelector(
      '.HugeHybridToggle__OptionBtn--selected'
    );
    this.value = selectedBtn.dataset.value;
  }

  connectedCallback() {
    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.cacheElements();
      this.attachListeners();
    }
  }

  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }

  attachListeners() {
    this.optionBtns.forEach(btn => {
      btn.addEventListener('click', this.onBtnClick);
    });

    // Add gesture events (useCapture to make certain it runs before clicks)
    this.addEventListener('mousedown', this.lock, { capture: true });
    this.addEventListener('touchstart', this.lock, { capture: true });
    this.addEventListener('mouseup', this.move, { capture: true });
    this.addEventListener('touchend', this.move, { capture: true });
  }

  detachListeners() {
    this.optionBtns.forEach(btn => {
      btn.removeEventListener('click', this.onBtnClick);
    });
    this.removeEventListener('mousedown', this.lock, { capture: true });
    this.removeEventListener('touchstart', this.lock, { capture: true });
    this.removeEventListener('mouseup', this.move, { capture: true });
    this.removeEventListener('touchend', this.move, { capture: true });
  }

  onBtnClick(e) {
    if (!this.userJustSwiped) {
      this.select(e.currentTarget);
      this.userJustSwiped = false;
    }
  }

  select(btn, silent = false) {
    this.value = btn.dataset.value;

    this.optionBtns.forEach(b =>
      b.classList.remove('HugeHybridToggle__OptionBtn--selected')
    );
    btn.classList.add('HugeHybridToggle__OptionBtn--selected');

    // Resize and move peg to the correct spot
    this.peg.style.width = `${btn.offsetWidth}px`;
    this.peg.style.transform = `translateX(${btn.offsetLeft}px)`;

    // Emit custom event
    if (!silent) {
      this.dispatchEvent(
        new CustomEvent('hybrid-toggle:change', {
          bubbles: true,
          detail: {
            value: this.value,
          },
        })
      );
    }
  }

  selectValue(value, silent = false) {
    const btn = this.querySelector(
      `.HugeHybridToggle__OptionBtn[data-value="${value}"]`
    );
    this.select(btn, silent);
  }

  /**
   * Store initial clientX for swipe events.
   */
  lock(e) {
    this.swipeX = this.unify(e).clientX;
  }

  /**
   * Determine swipe direction and select the appropriate button.
   */
  move(e) {
    if (this.swipeX || this.swipeX === 0) {
      const dx = this.unify(e).clientX - this.swipeX;

      // If the movement dx is large enough
      if (Math.abs(dx) > 5) {
        // Negative means they swiped left
        const s = Math.sign(dx);
        if (s < 0) {
          this.select(this.optionBtns[0]);
        } else {
          this.select(this.optionBtns[1]);
        }

        // Set this flag to cancel the click event that immediately follows.
        // (Sort of like e.stopPropagation.)
        this.userJustSwiped = true;
      } else {
        this.userJustSwiped = false;
      }

      this.swipeX = null;
    }
  }

  /**
   * Reconciles differences between touchstart and mousedown event objects.
   */
  unify(e) {
    return e.changedTouches ? e.changedTouches[0] : e;
  }
}
customElements.define('huge-hybrid-toggle', HugeHybridToggle);

/**
 * Uses the global nolah popup and loads contents into it.
 */
class HybridUpsellPopup {
  constructor(options) {
    if (options && options.onSelectColumn) {
      this.onSelectColumn = options.onSelectColumn;
    }

    // We will be using the global nolah popup to display info.
    this.popupEl = window.GlobalNPopup.popup;

    const btn = document.querySelector('[data-open-hybrid-popup]');
    btn.addEventListener('click', this.open.bind(this));
  }

  open() {
    // Load with contents and open the global popup
    const contentsEl = document.querySelector('#hybrid-upsell-popup-template');
    window.GlobalNPopup.loadWithContents(
      'hybrid-upsell-popup',
      contentsEl.innerHTML,
      {
        afterClose: this.cleanUp.bind(this),
      }
    );
    window.GlobalNPopup.open();

    // Initialize floating "Swipe Left" prompt for mobile
    this.initSwipeLeft();

    // Set active column (active column has an ATC button)
    const hybridCol = this.popupEl.querySelector(
      '.HybridUpsellPopup__Col--hybrid'
    );
    const foamCol = this.popupEl.querySelector('.HybridUpsellPopup__Col--foam');
    if (window.HybridUpsellState.selectedValue === 'hybrid') {
      this.initSelectedColumn(hybridCol);
      this.initUnselectedColumn(foamCol);
    } else {
      this.initSelectedColumn(foamCol);
      this.initUnselectedColumn(hybridCol);
    }

    // Set price labels for both columns
    this.updatePricesWithCurrentSize(
      hybridCol,
      window.HybridUpsellState.hybridProductJSON
    );
    this.updatePricesWithCurrentSize(
      foamCol,
      window.HybridUpsellState.foamProductJSON
    );
  }

  /**
   * Initialize mobile only swipe left graphic
   */
  initSwipeLeft() {
    // Bind and save function used with swipe left component.
    this.hideSwipeLeftIfFullyScrolled = NUtils.debounce(
      this.hideSwipeLeftIfFullyScrolled.bind(this),
      200
    );

    // Save the swipe left element so we can hide/unhide it later
    this.swipeLeftEl = this.popupEl.querySelector(
      '.HybridUpsellPopup__SwipeLeft'
    );

    // Save an instance of this container for removing the event listener later
    this.popupContainer = this.popupEl.querySelector(
      '.HybridUpsellPopup__Container'
    );

    this.popupContainer.addEventListener(
      'scroll',
      this.hideSwipeLeftIfFullyScrolled
    );
  }

  hideSwipeLeftIfFullyScrolled(e) {
    const element = e.target;
    if (
      Math.abs(
        element.scrollWidth - element.scrollLeft - element.clientWidth
      ) <= 5
    ) {
      this.swipeLeftEl.classList.add('HybridUpsellPopup__SwipeLeft--hidden');
    } else {
      this.swipeLeftEl.classList.remove('HybridUpsellPopup__SwipeLeft--hidden');
    }
  }

  cleanUp() {
    this.popupContainer.removeEventListener(
      'scroll',
      this.hideSwipeLeftIfFullyScrolled
    );
  }

  /**
   * Initializes the selected column, which is based on if the hybrid was checked
   * or not in the main product form.
   *
   * Selected columns have an eyeball svg and an ATC button.
   */
  initSelectedColumn(column) {
    column.classList.add('HybridUpsellPopup__Col--selected');

    const atcBtn = column.querySelector('.HybridUpsellPopup__Btn');
    atcBtn.dataset.buttonAction = 'ATC';
    atcBtn.addEventListener('click', e => {
      const mainATCBtn = document.querySelector('.MattressProductATCBtn__Btn');
      mainATCBtn.click();
      this.close();
    });
  }

  /**
   * Initialized the unselected column. Unselected columns have a select button.
   */
  initUnselectedColumn(column) {
    column.classList.remove('HybridUpsellPopup__Col--selected');

    const selectBtn = column.querySelector('.HybridUpsellPopup__Btn');
    selectBtn.dataset.buttonAction = 'Select';
    selectBtn.addEventListener('click', e => {
      const value = selectBtn.dataset.optionHandle;
      this.onSelectColumn && this.onSelectColumn(value);
      this.close();
    });
  }

  updatePricesWithCurrentSize(column, productJSON) {
    const priceEl = column.querySelector('[data-price-el]');
    const strikepriceEl = column.querySelector('[data-strikeprice-el]');
    const priceOffEl = column.querySelector('[data-price-off-el]');

    // Get the variant of the currently selected size to get prices from.
    // Then, get the price and strike price data
    const sizeKey = window.NolahPDPState.getOptionKey('Size');
    const currentSize = window.NolahPDPState.getOptionByName('Size');
    let matchingVar, price, strikePrice;
    if (window.NolahPDPState.pseudoSplitKingSelected) {
      matchingVar = productJSON.variants.find(v => v[sizeKey] === 'Twin XL');
      price = NUtils.getVariantPrice(matchingVar) * 2;
      strikePrice = NUtils.getVariantStrikePrice(matchingVar) * 2;
    } else {
      matchingVar = productJSON.variants.find(v => v[sizeKey] === currentSize);
      price = NUtils.getVariantPrice(matchingVar);
      strikePrice = NUtils.getVariantStrikePrice(matchingVar);
    }
    const priceOff = strikePrice - price;

    // Update the DOM
    priceEl.innerHTML = NUtils.formatMoney(price);
    strikepriceEl.innerHTML = NUtils.formatMoney(strikePrice);
    priceOffEl.innerHTML = NUtils.formatMoney(priceOff);
  }

  close() {
    window.GlobalNPopup.close();
  }
}

/**
 * The upsell buttons for hybrid buttons (og and sig).
 */
class MattressHybridArea {
  constructor(el) {
    this.el = el;
    if (!this.el) {
      return;
    }

    this.cacheElements();

    // This section appears under the size select on mobile
    this.showViewBasedOnWidth();
    this.mql.addListener(this.updateView);

    // Init buttons that select hybrid vs foam
    this.hybridUpsellBtns = new HybridUpsellButtons(this.el);

    // Init the blue 'hybrid' flag that papears when selected.
    this.initHybridSelectedTitle();

    // Init popup
    new HybridUpsellPopup({
      onSelectColumn: value => {
        window.HybridUpsellState.select(value);
      },
    });

    this.el.classList.add('MattressHybridArea--loaded');
  }

  cacheElements() {
    this.updateView = this.updateView.bind(this);

    // Media query list listener added when connected to DOM
    this.mql = window.matchMedia('(max-width: 768px)');
    this.isShowingMobileView = this.mql.matches;

    // This block is moved to a different location on mobile to match the designs
    this.mobileAnchorEl = document.querySelector(
      '[data-mobile-anchor="mattress-hybrid-area"]'
    );
    this.desktopAnchorEl = document.querySelector('#size-option-list-block');

    this.hybridSelectedTitle = this.el.querySelector(
      '.MattressHybridArea__HybridFlag'
    );
  }

  updateView(e) {
    this.isShowingMobileView = e.matches;
    this.showViewBasedOnWidth();
  }

  showViewBasedOnWidth() {
    if (this.isShowingMobileView) {
      this.showMobileView();
    } else {
      this.showDesktopView();
    }
  }

  showMobileView() {
    // Insert after the mobile anchor
    if (this.mobileAnchorEl) {
      this.mobileAnchorEl.parentNode.insertBefore(
        this.el,
        this.mobileAnchorEl.nextElementSibling
      );
    }
  }

  showDesktopView() {
    // Insert after the desktop anchor
    if (this.desktopAnchorEl) {
      this.desktopAnchorEl.parentNode.insertBefore(
        this.el,
        this.desktopAnchorEl.nextElementSibling
      );
    }
  }

  initHybridSelectedTitle() {
    this.updateHybridSelectedTitle();
    document.addEventListener('state:option-changed', e => {
      if (e && e.detail && e.detail.productJSONChanged) {
        this.updateHybridSelectedTitle();
      }
    });
  }

  updateHybridSelectedTitle() {
    if (window.HybridUpsellState.selectedValue === 'hybrid') {
      this.hybridSelectedTitle.classList.add(
        'MattressHybridArea__HybridFlag--selected'
      );
    } else {
      this.hybridSelectedTitle.classList.remove(
        'MattressHybridArea__HybridFlag--selected'
      );
    }
  }
}

/**
 * Miscellaneous mattress product page code that doesn't fit in a custom element.
 */
class MattressWithHybridPage {
  constructor() {
    // Init hybrid area if it exists
    const hybridArea = document.querySelector('.MattressHybridArea');
    if (hybridArea) {
      new MattressHybridArea(hybridArea);
    }
  }
}

new MattressWithHybridPage();
